import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Auth
export const authRegister = (payload) => api.post("/auth/register", payload);
export const authLogin = (payload) => api.post("/auth/login", payload);
export const authGoogle = (session_id) => api.post("/auth/google", { session_id });
export const authMe = () => api.get("/auth/me");
export const authLogout = () => api.post("/auth/logout");
export const updateProfile = (payload) => api.patch("/auth/profile", payload);
export const changePassword = (payload) => api.post("/auth/change-password", payload);
export const logCookieConsent = (payload) => api.post("/consent/cookies", payload);
export const backupStatus = () => api.get("/admin/backup/status");
export const runBackup = () => api.post("/admin/backup");
export const adminStats = () => api.get("/admin/stats");
// Stripe Connect creator payouts
export const connectStatus = () => api.get("/connect/status");
export const connectOnboard = (origin_url) => api.post("/connect/onboarding-link", { origin_url });
export const connectWithdraw = (amount_usd) => api.post("/connect/withdraw", { amount_usd });

// Account
export const accountOverview = () => api.get("/account/overview");
export const accountPurchases = () => api.get("/account/purchases");
export const accountProducts = () => api.get("/account/products");
export const accountPayments = () => api.get("/account/payments");
export const accountGenerations = (limit = 12) => api.get(`/account/generations?limit=${limit}`);

// Credits & payments
export const getPacks = () => api.get("/credits/packs");
export const getCosts = () => api.get("/credits/costs");
export const startCheckout = async (lookup_key) => {
  // Rewardful affiliate referral (only sent when present, to avoid empty Stripe values)
  let client_reference_id;
  try {
    if (typeof window !== "undefined" && window.Rewardful && window.Rewardful.referral) {
      client_reference_id = window.Rewardful.referral;
    }
  } catch (e) { /* Rewardful not loaded */ }
  const { data } = await api.post("/payments/checkout", {
    lookup_key,
    quantity: 1,
    origin_url: window.location.origin,
    ...(client_reference_id ? { client_reference_id } : {}),
  });
  if (data.checkout_url) window.location.href = data.checkout_url;
};
export const getPaymentStatus = (session_id) => api.get(`/payments/status/${session_id}`);

// PayPal
export const paypalConfig = () => api.get("/paypal/config");
export const paypalCreateOrder = (lookup_key) => api.post("/paypal/create-order", { lookup_key });
export const paypalCaptureOrder = (order_id) => api.post("/paypal/capture-order", { order_id });

// Misc
export const sendContact = (payload) => api.post("/contact", payload);

// File uploads (object storage)
export const uploadFile = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
};
export const absFileUrl = (path) => `${API}/files/${path}`;

// Robust cross-browser "save file" — fetches as blob then triggers download.
export const downloadFromUrl = async (url, filename) => {
  // Our own API file URLs (/api/files/...) are private and need the auth cookie.
  // External URLs (fal.ai, data URLs) must NOT send credentials to avoid CORS failures.
  const isInternal = url.startsWith("/") || url.startsWith(API);
  const res = await fetch(url, { credentials: isInternal ? "include" : "omit" });
  if (!res.ok) throw new Error("download failed");
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename || "aiba-file";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
};

// AI
export const aiText = (tool_id, prompt, model, image, session_id) => api.post("/ai/text", { tool_id, prompt, model, image, session_id });
export const aiImage = (tool_id, prompt, consent = false) => api.post("/ai/image", { tool_id, prompt, consent });
export const aiVoice = (text, voice) => api.post("/ai/voice", { text, voice });
export const aiVideo = (prompt, duration, image_url, engine) => api.post("/ai/video", { prompt, duration, image_url, engine });
export const videoJob = (jobId) => api.get(`/ai/video/job/${jobId}`);
export const videoEngines = () => api.get("/ai/video/engines");
export const videoQuota = () => api.get("/ai/video/quota");
export const aiHistory = (tool_id) => api.get(`/ai/history/${tool_id}`);
export const logSaveConsent = (tool_id, kind) => api.post("/consent/save", { tool_id, kind });

// Community
export const listPosts = () => api.get("/community/posts");
export const createPost = (content, media, media_type) =>
  api.post("/community/posts", { content, media, media_type });
export const likePost = (id) => api.post(`/community/posts/${id}/like`);
export const reportPost = (id) => api.post(`/community/posts/${id}/report`);
export const deletePost = (id) => api.delete(`/community/posts/${id}`);
export const reportedPosts = () => api.get("/community/reported");
export const moderatePost = (id) => api.post(`/community/posts/${id}/moderate`);
export const listReplies = (id) => api.get(`/community/posts/${id}/replies`);
export const createReply = (id, content) => api.post(`/community/posts/${id}/reply`, { content });

// Notifications
export const getNotifications = () => api.get("/notifications");
export const readNotifications = () => api.post("/notifications/read");

// Digital products marketplace
export const listProducts = () => api.get("/products");
export const createProduct = (payload) => api.post("/products", payload);
export const buyProduct = (id) =>
  api.post(`/products/${id}/buy?origin_url=${encodeURIComponent(window.location.origin)}`);
export const buyProductWithCredits = (id) => api.post(`/products/${id}/buy-credits`);
export const boostProduct = (id) => api.post(`/products/${id}/boost`);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const marketplaceConfig = () => api.get("/marketplace/config");
export const downloadProduct = (id) => api.get(`/products/${id}/download`, { responseType: "blob" });

// Animator Film (galerie communautaire de films payants)
export const listFilms = (sort = "new") => api.get(`/films?sort=${sort}`);
export const recommendedFilms = () => api.get("/films/recommended");
export const deleteFilm = (id) => api.delete(`/films/${id}`);
export const getFilm = (id) => api.get(`/films/${id}`);
export const createFilm = (payload) => api.post("/films", payload);
export const buyFilm = (id, tier = "watch") => api.post(`/films/${id}/buy`, { tier });
export const likeFilm = (id) => api.post(`/films/${id}/like`);
export const getFilmComments = (id) => api.get(`/films/${id}/comments`);
export const addFilmComment = (id, content) => api.post(`/films/${id}/comments`, { content });
export const filmVideoUrl = (id) => `${API}/films/${id}/video`;

export function formatApiError(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join(" ");
  if (detail.msg) return detail.msg;
  return String(detail);
}
